import React, { useState, useMemo, useCallback } from 'react'
import MatchCard from '../matchCard/matchCard'
import styles from './matchesPage.module.css'
import Header from "../header/header";
import CalendarDropdown from "../calendarDropdown/calendarDropdown";
import {useGetFutureMatches, useGetMatches, useGetPastMatches} from "../../hooks/useGetMatches"
import { Match } from "../../types/matchTypes"

const MATCHES_COUNT = 10;

const MatchesPage: React.FC = () => {
	const formatDate = (dateString: string): string => {
		const date = new Date(dateString)
		const day = date.getDate()
		const monthIndex = date.getMonth()
		const weekdayIndex = date.getDay()
		const months = [
			'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
			'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'
		]
		const weekdays = [
			'воскресенье', 'понедельник', 'вторник', 'среда',
			'четверг', 'пятница', 'суббота'
		]
		return `${day} ${months[monthIndex]}, ${weekdays[weekdayIndex]}`
	}
	const initDateRange = (): [Date, Date] => {
		const now = new Date();
		const startOfDay = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 0, 0, 0, 0));
		const endOfDay = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 23, 59, 59, 999));
		return [startOfDay, endOfDay];
	};

	const [selectedDateRange, setSelectedDateRange] = useState<[Date, Date]>(initDateRange());
	const [selectedTeamId, setSelectedTeamId] = useState<number | null>(null);
	const [selectedTournamentId, setSelectedTournamentId] = useState<number | null>(null);

	const options = useMemo(() => ({
		startDate: selectedDateRange[0],
		endDate: selectedDateRange[1],
		teamId: selectedTeamId || undefined,
		tournamentId: selectedTournamentId || undefined
	}), [selectedDateRange, selectedTeamId, selectedTournamentId]);

	const {
		matches: currentMatches,
		loading: currentLoading,
		error: currentError
	} = useGetMatches(options);

	const additionalOptions = useMemo(() => ({
		teamId: selectedTeamId || undefined,
		tournamentId: selectedTournamentId || undefined
	}), [selectedTeamId, selectedTournamentId]);

	const {
		matches: futureMatches,
		loading: futureLoading,
		error: futureError
	} = useGetFutureMatches(MATCHES_COUNT, additionalOptions);

	const {
		matches: pastMatches,
		loading: pastLoading,
		error: pastError
	} = useGetPastMatches(MATCHES_COUNT, additionalOptions);

	const loading = currentLoading || futureLoading || pastLoading;
	const error = currentError || futureError || pastError;

	const matches = useMemo(() => {
		if (currentMatches.length > 0) return currentMatches;
		if (futureMatches.length > 0) return futureMatches;
		return pastMatches;
	}, [currentMatches, futureMatches, pastMatches]);


	const handleDateSelect = useCallback((date: Date | [Date, Date] | null) => {
		console.log('Selected date in MatchesPage:', date);
		if (Array.isArray(date)) {
			setSelectedDateRange([new Date(date[0]), new Date(date[1])]);
		} else if (date instanceof Date) {
			const endOfDay = new Date(date);
			endOfDay.setUTCHours(23, 59, 59, 999);
			setSelectedDateRange([new Date(date), endOfDay]);
		} else {
			setSelectedDateRange(initDateRange());
		}
	}, []);

	const groupedMatches = useMemo(() => {
		console.log('Grouping matches, count:', matches.length);
		return matches.reduce((acc: {[key: string]: {[key: string]: Match[]}}, match) => {
			const dateKey = match.actual_start_time.split('T')[0];
			const tournamentKey = match.tournament_id?.toString() || 'unknown';

			if (!acc[dateKey]) {
				acc[dateKey] = {};
			}
			if (!acc[dateKey][tournamentKey]) {
				acc[dateKey][tournamentKey] = [];
			}
			acc[dateKey][tournamentKey].push(match);
			return acc;
		}, {});
	}, [matches]);

	console.log('Rendering MatchesPage. GroupedMatches:', Object.keys(groupedMatches).length);

	return (
		<>
			<Header title={'Matches'}/>
			<main>
				<CalendarDropdown
					onDateSelect={handleDateSelect}
					allowRangeSelection={true}
					buttonText="Выбрать дату"
					initialDateRange={selectedDateRange}
				/>
				<div className={styles.match__list}>
					{loading ? (
						<p>Loading matches...</p>
					) : error ? (
						<p>Error loading matches: {error.message}</p>
					) : Object.keys(groupedMatches).length === 0 ? (
						<p>No matches found for the selected date range.</p>
					) : (
						<div className={styles.calendar__container}>
							{Object.entries(groupedMatches).map(([date, tournamentMatches]) => (
								<div key={date}>
									<h2 className={styles.date__header}>{formatDate(date)}</h2>
									{Object.entries(tournamentMatches).map(([tournamentId, matches]) => (
										<div key={tournamentId}>
											<h3 className={styles.tournament__header}>
												{matches[0]?.tournament_short_name || matches[0]?.tournament_name || 'Unknown Tournament'}
											</h3>
											<ul>
												{matches.map((match) => (
													<MatchCard key={match.id} match={match} />
												))}
											</ul>
										</div>
									))}
								</div>
							))}
						</div>
					)}
				</div>
			</main>
		</>
	);
};

export default React.memo(MatchesPage);




